<template>  
    <div class="page container p-4 py-4">

      <!-- ── Tela de boas-vindas (após cadastro) ─────────────── -->
      <div v-if="registrationSuccess" class="welcome-screen text-center py-5">

        <div class="welcome-icon mb-4">
          <div class="welcome-icon__circle">
            <i class="fas fa-check welcome-icon__check"></i>
          </div>
        </div>

        <h2 class="welcome-title mb-2">
          Conta criada, {{ firstName }}! 🎉
        </h2>
        <p class="text-muted mb-4">
          Seu cadastro já está completo. Você já pode <strong>criar listas e compartilhar com seus convidados</strong>.
        </p>

        <div class="onboarding-progress mb-4">
          <div class="onboarding-progress__step onboarding-progress__step--done">
            <div class="onboarding-progress__dot onboarding-progress__dot--done">
              <i class="fas fa-check"></i>
            </div>
            <span>Conta criada</span>
          </div>
          <div class="onboarding-progress__line onboarding-progress__line--done"></div>
          <div class="onboarding-progress__step onboarding-progress__step--active">
            <div class="onboarding-progress__dot onboarding-progress__dot--active">2</div>
            <span>Dados pessoais</span>
          </div>
          <div class="onboarding-progress__line"></div>
          <div class="onboarding-progress__step">
            <div class="onboarding-progress__dot">3</div>
            <span>Pronto!</span>
          </div>
        </div>

        <div class="trust-list mb-4">
          <div class="trust-list__item">
            <i class="fas fa-shield-alt trust-list__icon"></i>
            <span>Seus dados ficam protegidos em ambiente seguro</span>
          </div>
          <div class="trust-list__item">
            <i class="fas fa-lock trust-list__icon"></i>
            <span>Seus dados são protegidos e nunca compartilhados</span>
          </div>
          <div class="trust-list__item">
            <i class="fas fa-bolt trust-list__icon"></i>
            <span>Necessário para criar sua primeira lista de presentes</span>
          </div>
        </div>

        <button @click="continueToDashboard" class="btn btn-color-site btn-lg w-100">
          <i class="fas fa-arrow-right me-2"></i>Ir para meu painel
        </button>

        <p class="text-muted small mt-3">Você pode configurar os recebimentos no momento de publicar ou compartilhar sua lista.</p>

      </div>

      <!-- ── Formulário de cadastro ─────────────────────────── -->
      <template v-else>
      <section class="register-hero text-center mb-4">
        <span class="section-eyebrow">Crie sua conta</span>
        <h2 class="mb-2 text-primary-emphasis">Criar sua conta</h2>
        <p class="text-muted mb-0 description-text">Junte-se a nós! Preencha seus dados para criar sua conta.</p>
      </section>

      <form @submit.prevent="register" class="register-form" novalidate>

        <div class="field-group mb-3">
          <label class="field-label">Nome Completo</label>
          <input
            v-model="form.name"
            type="text"
            :class="['form-control register-input', { 'is-invalid': errors.name }]"
            placeholder="Seu nome completo"
            required
          />
          <div v-if="errors.name" class="field-error">{{ errors.name }}</div>
        </div>

        <div class="row g-2 mb-3">
          <div class="col-12 col-md-6">
            <label class="field-label">E-mail</label>
            <input
              v-model="form.email"
              type="email"
              :class="['form-control register-input', { 'is-invalid': errors.email }]"
              placeholder="seu@email.com"
              required
            />
            <div v-if="errors.email" class="field-error">{{ errors.email }}</div>
          </div>
          <div class="col-12 col-md-6">
            <label class="field-label">WhatsApp</label>
            <input
              v-model="form.phone"
              type="tel"
              :class="['form-control register-input', { 'is-invalid': errors.phone }]"
              placeholder="(11) 99999-9999"
              maxlength="15"
              @input="handlePhoneInput"
              required
            />
            <div v-if="errors.phone" class="field-error">{{ errors.phone }}</div>
          </div>
        </div>

        <div class="row g-2 mb-4">
          <div class="col-12 col-md-6">
            <label class="field-label">CPF</label>
            <input
              v-model="form.document"
              type="text"
              :class="['form-control register-input', { 'is-invalid': errors.document }]"
              placeholder="999.999.999-99"
              maxlength="14"
              @input="handleDocumentInput"
              required
            />
            <div v-if="errors.document" class="field-error">{{ errors.document }}</div>
          </div>
          <div class="col-12 col-md-6">
            <label class="field-label">Data de nascimento</label>
            <input
              v-model="form.birth_date"
              type="date"
              :class="['form-control register-input', { 'is-invalid': errors.birth_date }]"
              :max="maxBirthDate"
              required
            />
            <div v-if="errors.birth_date" class="field-error">{{ errors.birth_date }}</div>
          </div>
        </div>

        <div class="row g-2 mb-4">
          <div class="col-12 col-md-6">
            <label class="field-label">Senha</label>
            <div class="password-field-wrapper">
              <input
                v-model="form.password"
                :type="passwordFieldType"
                :class="['form-control register-input', { 'is-invalid': errors.password }]"
                placeholder="Mínimo 6 caracteres"
                required
              />
              <button
                type="button"
                class="password-toggle-btn"
                tabindex="-1"
                @click="togglePasswordVisibility"
              >
                <i :class="passwordToggleIcon"></i>
              </button>
            </div>
            <div v-if="errors.password" class="field-error">{{ errors.password }}</div>
          </div>
          <div class="col-12 col-md-6">
            <label class="field-label">Confirmar Senha</label>
            <div class="password-field-wrapper">
              <input
                v-model="confirmPassword"
                :type="confirmPasswordFieldType"
                :class="['form-control register-input', { 'is-invalid': errors.confirmPassword }]"
                placeholder="Repita a senha"
                required
              />
              <button
                type="button"
                class="password-toggle-btn"
                tabindex="-1"
                @click="toggleConfirmPasswordVisibility"
              >
                <i :class="confirmPasswordToggleIcon"></i>
              </button>
            </div>
            <div v-if="errors.confirmPassword" class="field-error">{{ errors.confirmPassword }}</div>
          </div>
        </div>

        <div class="mb-3 d-flex align-items-start gap-2 terms-box" :class="{ 'terms-box--error': termsValidationError }">
          <input
            id="terms-register"
            v-model="termsAccepted"
            type="checkbox"
            class="form-check-input mt-1 flex-shrink-0"
            @change="handleTermsChange"
          />
          <label for="terms-register" class="form-check-label small text-muted">
            Li e aceito os
            <a href="/termos-de-uso" target="_blank" class="text-pink">Termos de Uso</a>
            e a
            <a href="/politica-de-privacidade" target="_blank" class="text-pink">Política de Privacidade</a>
            da QueMimo.
          </label>
        </div>

        <div class="d-grid">
          <button type="submit" :disabled="loading" class="btn btn-color-site btn-lg register-btn">
            <span v-if="loading">
              <i class="fas fa-spinner fa-spin me-2"></i> Cadastrando...
            </span>
            <span v-else>
              <i class="fas fa-user-plus me-2"></i> Cadastrar
            </span>
          </button>
        </div>

        <div class="social-divider my-3">
          <span>ou</span>
        </div>

        <div class="d-grid">
          <button
            type="button"
            class="btn btn-google-fallback w-100"
            @click="openGooglePopup"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" width="18" height="18" alt="" class="me-2" />
            Cadastrar com sua conta Google
          </button>
        </div>

        <div class="d-grid mt-2">
          <button
            type="button"
            class="btn btn-facebook w-100"
            @click="openFacebookPopup"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" class="me-2"><path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.592 1.323-1.324V1.324C24 .593 23.408 0 22.676 0"/></svg>
            Cadastrar com Facebook
          </button>
        </div>

        <div class="d-flex flex-column gap-2 mt-4 mb-5 register-links">
          <router-link to="/login" class="text-link">Já tem uma conta? <b>Entrar</b></router-link>
        </div>

      </form>
      </template>

    </div>
  
</template>

<script>
import { useAuthStore } from '@/stores/authStore';
import { parseError } from '@/utils/errorHandler';
import api from '@/services/api';
import { toast } from 'vue3-toastify';
import { logAcceptance } from '@/services/complianceService';

export default {
  name: 'Register',
  data() {
    return {
      form: {
        name: '',
        email: '',
        phone: '',
        document: '',
        birth_date: '',
        password: '',
      },
      confirmPassword: '',
      loading: false,
      passwordVisible: false,
      confirmPasswordVisible: false,
      errors: {},
      registrationSuccess: false,
      firstName: '',
      termsAccepted: false,
      termsValidationError: false,
      googleNonce: Math.random().toString(36).slice(2) + Date.now().toString(36),
    };
  },
  computed: {
    passwordFieldType() {
      return this.passwordVisible ? 'text' : 'password';
    },
    passwordToggleIcon() {
      return this.passwordVisible ? 'fas fa-eye-slash' : 'fas fa-eye';
    },
    confirmPasswordFieldType() {
      return this.confirmPasswordVisible ? 'text' : 'password';
    },
    confirmPasswordToggleIcon() {
      return this.confirmPasswordVisible ? 'fas fa-eye-slash' : 'fas fa-eye';
    },
    maxBirthDate() {
      const now = new Date();
      now.setFullYear(now.getFullYear() - 18);
      return now.toISOString().slice(0, 10);
    },
    googleAuthUrl() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId) return '#';
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: `${window.location.origin}/login`,
        response_type: 'id_token',
        scope: 'openid email profile',
        nonce: this.googleNonce,
        prompt: 'select_account',
      });
      return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    },
    facebookAuthUrl() {
      const appId = process.env.VUE_APP_FACEBOOK_APP_ID;
      if (!appId) return '#';
      const params = new URLSearchParams({
        client_id: appId,
        redirect_uri: `${window.location.origin}/login`,
        response_type: 'token',
        scope: 'email,public_profile',
        display: 'popup',
      });
      return `https://www.facebook.com/v19.0/dialog/oauth?${params}`;
    },
  },
  mounted() {
    this.handleGoogleRedirectCallback();
    this.handleFacebookRedirectCallback();
  },
  beforeUnmount() {
    if (this._googleMsgHandler) window.removeEventListener('message', this._googleMsgHandler);
    if (this._googlePopupWatcher) clearInterval(this._googlePopupWatcher);
    if (this._facebookMsgHandler) window.removeEventListener('message', this._facebookMsgHandler);
    if (this._facebookPopupWatcher) clearInterval(this._facebookPopupWatcher);
  },
  methods: {
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
    handleGoogleRedirectCallback() {
      const inPopup = !!(window.opener && window.opener !== window);
      const hash = window.location.hash;
      const idToken = hash ? new URLSearchParams(hash.slice(1)).get('id_token') : null;
      if (inPopup) {
        if (idToken) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
          window.opener.postMessage({ type: 'google-auth', idToken }, window.location.origin);
        }
        window.close();
        return;
      }
    },
    handleFacebookRedirectCallback() {
      const inPopup = !!(window.opener && window.opener !== window);
      const hash = window.location.hash;
      const accessToken = hash ? new URLSearchParams(hash.slice(1)).get('access_token') : null;
      if (inPopup) {
        if (accessToken) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
          window.opener.postMessage({ type: 'facebook-auth', accessToken }, window.location.origin);
        }
        window.close();
        return;
      }
    },
    openGooglePopup() {
      const url = this.googleAuthUrl;
      if (!url || url === '#') return;
      if (this._googleMsgHandler) {
        window.removeEventListener('message', this._googleMsgHandler);
        this._googleMsgHandler = null;
      }
      if (this._googlePopupWatcher) {
        clearInterval(this._googlePopupWatcher);
        this._googlePopupWatcher = null;
      }
      const w = 500, h = 620;
      const left = Math.round((screen.width - w) / 2);
      const top = Math.round((screen.height - h) / 2);
      const popup = window.open(url, 'google-oauth', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (!popup) {
        window.location.href = url;
        return;
      }
      const handler = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'google-auth') return;
        clearInterval(this._googlePopupWatcher);
        this._googlePopupWatcher = null;
        window.removeEventListener('message', handler);
        this._googleMsgHandler = null;
        this.handleGoogleCredential({ credential: event.data.idToken });
      };
      window.addEventListener('message', handler);
      this._googleMsgHandler = handler;
      this._googlePopupWatcher = setInterval(() => {
        if (popup.closed) {
          clearInterval(this._googlePopupWatcher);
          this._googlePopupWatcher = null;
          window.removeEventListener('message', this._googleMsgHandler);
          this._googleMsgHandler = null;
        }
      }, 500);
    },
    openFacebookPopup() {
      const url = this.facebookAuthUrl;
      if (!url || url === '#') return;
      if (this._facebookMsgHandler) {
        window.removeEventListener('message', this._facebookMsgHandler);
        this._facebookMsgHandler = null;
      }
      if (this._facebookPopupWatcher) {
        clearInterval(this._facebookPopupWatcher);
        this._facebookPopupWatcher = null;
      }
      const w = 500, h = 620;
      const left = Math.round((screen.width - w) / 2);
      const top = Math.round((screen.height - h) / 2);
      const popup = window.open(url, 'facebook-oauth', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (!popup) {
        window.location.href = url;
        return;
      }
      const handler = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'facebook-auth') return;
        clearInterval(this._facebookPopupWatcher);
        this._facebookPopupWatcher = null;
        window.removeEventListener('message', handler);
        this._facebookMsgHandler = null;
        this.handleFacebookCredential(event.data.accessToken);
      };
      window.addEventListener('message', handler);
      this._facebookMsgHandler = handler;
      this._facebookPopupWatcher = setInterval(() => {
        if (popup.closed) {
          clearInterval(this._facebookPopupWatcher);
          this._facebookPopupWatcher = null;
          window.removeEventListener('message', this._facebookMsgHandler);
          this._facebookMsgHandler = null;
        }
      }, 500);
    },
    async handleFacebookCredential(accessToken) {
      const auth = useAuthStore();
      this.loading = true;
      try {
        const res = await api.post('/auth/facebook/callback', { access_token: accessToken });
        auth.setTokenAndDecodeUser(res.data.token, res.data);
        if (res.data.isNewUser) {
          auth.setOnboardingMode(true);
          this.$router.push('/my-account/profile');
        } else {
          this.$router.push('/my-account/dashboard');
        }
      } catch (error) {
        toast.error(parseError(error) || 'Erro ao autenticar com o Facebook.', {
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        this.loading = false;
      }
    },
    async handleGoogleCredential(response) {
      const auth = useAuthStore();
      this.loading = true;
      try {
        const res = await api.post('/auth/google/callback', { id_token: response.credential });
        auth.setTokenAndDecodeUser(res.data.token, res.data);
        if (res.data.isNewUser) {
          auth.setOnboardingMode(true);
          this.$router.push('/my-account/profile');
        } else {
          this.$router.push('/my-account/dashboard');
        }
      } catch (error) {
        toast.error(parseError(error) || 'Erro ao autenticar com o Google.', {
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        this.loading = false;
      }
    },
    toggleConfirmPasswordVisibility() {
      this.confirmPasswordVisible = !this.confirmPasswordVisible;
    },
    handleTermsChange() {
      if (this.termsAccepted) {
        this.termsValidationError = false;
      }
    },
    handlePhoneInput(event) {
      this.form.phone = this.applyPhoneMask(event.target.value);
    },
    handleDocumentInput(event) {
      this.form.document = this.applyDocumentMask(event.target.value);
    },
    applyPhoneMask(value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
      if (digits.length <= 2) return digits;
      if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    },
    applyDocumentMask(value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
      if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    },
    isValidCPF(value) {
      const cpf = String(value || '').replace(/\D/g, '');
      if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

      let sum = 0;
      for (let index = 0; index < 9; index += 1) {
        sum += Number(cpf[index]) * (10 - index);
      }
      let digit = (sum * 10) % 11;
      if (digit === 10) digit = 0;
      if (digit !== Number(cpf[9])) return false;

      sum = 0;
      for (let index = 0; index < 10; index += 1) {
        sum += Number(cpf[index]) * (11 - index);
      }
      digit = (sum * 10) % 11;
      if (digit === 10) digit = 0;

      return digit === Number(cpf[10]);
    },
    isAdult(dateString) {
      if (!dateString) return false;
      const birthDate = new Date(`${dateString}T00:00:00`);
      if (Number.isNaN(birthDate.getTime())) return false;

      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
      }

      return age >= 18;
    },
    continueToDashboard() {
      const auth = useAuthStore();
      auth.setOnboardingMode(false);
      this.$router.push('/my-account/dashboard');
    },
    validateForm() {
      this.errors = {};
      this.termsValidationError = false;
      let isValid = true;

      if (!this.form.name.trim()) {
        this.errors.name = 'O nome completo é obrigatório.';
        isValid = false;
      } else if (this.form.name.trim().length < 3) {
        this.errors.name = 'O nome completo deve ter pelo menos 3 caracteres.';
        isValid = false;
      }

      if (!this.form.email.trim()) {
        this.errors.email = 'O e-mail é obrigatório.';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.errors.email = 'Formato de e-mail inválido.';
        isValid = false;
      }

      if (!this.form.phone.trim()) {
        this.errors.phone = 'O WhatsApp é obrigatório.';
        isValid = false;
      } else if (!/^\(?\d{2}\)?\s?9?\d{4}-?\d{4}$/.test(this.form.phone.trim())) {
        this.errors.phone = 'Formato de WhatsApp inválido. Ex: (11) 99999-9999.';
        isValid = false;
      }

      if (!this.form.document.trim()) {
        this.errors.document = 'O CPF é obrigatório.';
        isValid = false;
      } else if (!this.isValidCPF(this.form.document)) {
        this.errors.document = 'CPF inválido.';
        isValid = false;
      }

      if (!this.form.birth_date) {
        this.errors.birth_date = 'A data de nascimento é obrigatória.';
        isValid = false;
      } else if (!this.isAdult(this.form.birth_date)) {
        this.errors.birth_date = 'É necessário ter pelo menos 18 anos para se cadastrar.';
        isValid = false;
      }

      if (!this.form.password) {
        this.errors.password = 'A senha é obrigatória.';
        isValid = false;
      } else if (this.form.password.length < 6) {
        this.errors.password = 'A senha deve ter pelo menos 6 caracteres.';
        isValid = false;
      }

      if (!this.confirmPassword) {
        this.errors.confirmPassword = 'A confirmação de senha é obrigatória.';
        isValid = false;
      } else if (this.form.password !== this.confirmPassword) {
        this.errors.confirmPassword = 'As senhas não coincidem.';
        isValid = false;
      }

      if (!this.termsAccepted) {
        this.termsValidationError = true;
        toast.error('É obrigatório aceitar os Termos de Uso e a Política de Privacidade.', {
          autoClose: 3000,
          position: 'top-right',
        });
        isValid = false;
      }

      return isValid;
    },
    async register() {
      if (!this.validateForm()) {
        if (Object.keys(this.errors).length > 0) {
          toast.error('Por favor, corrija os erros no formulário.', {
            autoClose: 3000,
            position: 'top-right',
          });
        }
        return;
      }

      const auth = useAuthStore();
      this.loading = true;

      try {
        const payload = {
          name: this.form.name,
          email: this.form.email,
          phone: this.form.phone,
          cpf_document: this.form.document,
          birth_date: this.form.birth_date,
          password: this.form.password,
        };

        const response = await api.post('/users', payload);
        auth.setTokenAndDecodeUser(response.data.token);

        try {
          await logAcceptance('terms_of_use');
        } catch (e) {
          console.warn('[compliance] falha ao registrar aceite terms_of_use', e);
        }

        const registerData = {
          name: this.form.name.trim(),
          email: this.form.email.trim(),
        };
        sessionStorage.setItem('register_data', JSON.stringify(registerData));
        auth.setOnboardingMode(false);
        await this.$router.replace('/my-account/dashboard');
      } catch (error) {
        const friendlyMessage = parseError(error);
        toast.error(friendlyMessage || 'Ocorreu um erro ao tentar se cadastrar.', {
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        this.loading = false;
      }
    },
  }
};
</script>

<style>
main{
  overflow-x: hidden;
}
</style>

<style scoped>
/* Cores e fontes gerais */
:root {
  --primary-color: #ff50a3; /* Sua cor rosa principal (do design) */
  --secondary-color: #32355d; /* Sua cor azul escuro (do design) */
  --text-muted-color: #6c757d; /* Cor para texto suave */
  --button-default-color: #F8F9FA; /* Nova cor para o botão de "Entrar" */
  --button-default-text-color: #6c757d; /* Cor do texto do botão de "Entrar" */
  --button-default-border-color: #ced4da; /* Cor da borda do botão de "Entrar" */
}

.text-primary-emphasis {
  color: var(--secondary-color) !important;
}

.section-eyebrow {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #ff50a3;
  margin-bottom: 0.4rem;
}


.page {  
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  height: auto;
  width: 600px;
  max-width: 700px;
  margin: 0 auto;
  align-self: center;
}

@media (max-width: 767.98px) {
  .page {   
    width: 100%; 
    margin-top: 0rem;
    min-width: inherit;
  }
}

/* Padding interno equivalente ao step-content-wrapper de Meus Dados */
form {
  padding: 0 1rem;
}

h2 {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: var(--secondary-color) !important;
  text-align: center;
}

.description-text {
  font-size: 0.95rem;
  line-height: 1.4;
  color: var(--text-muted-color);
  margin-bottom: 1.5rem;
  text-align: center;
}

.register-links .text-link {
  font-size: 0.95rem;
}

.form-label {
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
  display: block;
}

/* ── Input padrão ────────────────────────────────────────── */
.form-control {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control::placeholder {
  color: #b0b0b0;
  font-size: 0.9rem;
}

.form-control:focus {
  background-color: #ffffff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 0.2rem rgba(255, 80, 163, 0.18);
  outline: none;
  color: #32355d;
}

/* ── Password field wrapper ──────────────────────────────── */
.password-field-wrapper {
  position: relative;
}

.password-field-wrapper .register-input {
  padding-right: 3rem;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  color: #9e9e9e;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
  z-index: 2;
}

.password-toggle-btn:hover {
  color: #ff50a3;
}

.password-toggle-btn:focus,
.password-toggle-btn:active {
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

/* Mensagens de erro de validação */
.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  color: #dc3545 !important;
}

/* Botão principal (Cadastrar) */
.btn-color-site {
  background-color: #ff50a3;
  color: #fff; /* Garantir que o texto inicial seja branco */
  border: 1px solid #ff50a3;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}

.btn-color-site:hover:not(:disabled) {
  background-color: #e64593; /* Um tom mais escuro de rosa */
  border-color: #e64593;     /* Borda também mais escura */
  color: #fff;              /* Garantir que o texto permaneça branco no hover */
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.btn-color-site:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.fa-spinner {
  vertical-align: -0.125em;
}

.text-link {
  font-size: 1rem;
  color: var(--secondary-color);
  text-decoration: none;
  transition: color 0.2s ease-in-out;
}

.text-link:hover {
  color: var(--primary-color);
  text-decoration: underline;
}

.text-link b {
  font-weight: 700;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  white-space: nowrap;
}
.btn i.me-2 {
  margin-right: 0.5rem;
}

@media (max-width: 767.98px) {
  h2 {
    font-size: 1.6rem;
  }

  .description-text {
    font-size: 0.92rem;
  }
}

/* ── Welcome Screen ──────────────────────────────── */
.welcome-screen {
  max-width: 480px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.welcome-icon__circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff50a3, #e6408f);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  animation: welcome-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.welcome-icon__check {
  color: #fff;
  font-size: 2rem;
}
@keyframes welcome-pop {
  0%   { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.welcome-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #32355d;
}

/* Barra de progresso do onboarding */
.onboarding-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 1rem;
}
.onboarding-progress__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  color: #adb5bd;
  white-space: nowrap;
}
.onboarding-progress__step--done   { color: #ff50a3; }
.onboarding-progress__step--active { color: #32355d; font-weight: 700; }

.onboarding-progress__dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e9ecef;
  color: #adb5bd;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.onboarding-progress__dot--done {
  background: #ff50a3;
  color: #fff;
}
.onboarding-progress__dot--active {
  background: #32355d;
  color: #fff;
  box-shadow: 0 0 0 3px rgba(50, 53, 93, 0.15);
}

.onboarding-progress__line {
  flex: 1;
  height: 2px;
  background: #e9ecef;
  min-width: 30px;
  max-width: 60px;
  margin-bottom: 1.2rem;
}
.onboarding-progress__line--done { background: #ff50a3; }

/* Lista de confiança */
.trust-list {
  text-align: left;
  background: #f8f9fa;
  border-radius: 0.875rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.trust-list__item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.875rem;
  color: #555;
}
.trust-list__icon {
  color: #ff50a3;
  font-size: 0.85rem;
  flex-shrink: 0;
  width: 16px;
  text-align: center;
}

.onboarding-modal-content h5 {
  color: #32355d;
  font-weight: 700;
}

/* ── Layout do formulário ───────────────────────────────── */
.register-form {
  padding: 0 0.5rem;
}

.field-group {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.3rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.field-error {
  font-size: 0.78rem;
  color: #e0185d;
  margin-top: 0.25rem;
}

.terms-box {
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff7fb;
  border: 1px solid #f5dbe8;
}

.terms-box--error {
  border: 1px dashed #e84c7a;
  background: #fff1f6;
}

/* ── Input elegante ─────────────────────────────────────── */
.register-input {
  background-color: #fafafa;
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.register-input:focus {
  background-color: #fff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
  outline: none;
}

.register-input::placeholder {
  color: #c0c0c0;
  font-size: 0.88rem;
}

/* type="date" não suporta ::placeholder — aplica cinza enquanto vazio */
input[type="date"].register-input:not(:valid) {
  color: #c0c0c0;
}

.register-input.is-invalid {
  border-color: #e0185d !important;
  box-shadow: 0 0 0 2px rgba(224, 24, 93, 0.08);
}

.register-input.is-invalid:focus {
  border-color: #e0185d !important;
  box-shadow: 0 0 0 3px rgba(224, 24, 93, 0.15);
}

/* ── Botão principal ─────────────────────────────────────── */
.register-btn {
  border-radius: 0.875rem;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.85rem;
  letter-spacing: 0.03em;
  background: linear-gradient(135deg, #ff50a3 0%, #e0185d 100%);
  border: none;
  color: #fff;
  transition: opacity 0.2s, transform 0.1s;
}

.register-btn:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.register-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Password toggle (duplicate block removed — handled above) ── */

/* ── Links ──────────────────────────────────────────────── */
.text-link {
  color: #ff50a3;
  text-decoration: none;
  font-size: 0.9rem;
  text-align: center;
}

.text-link:hover {
  text-decoration: underline;
}

.social-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #adb5bd;
  font-size: 0.85rem;
}
.social-divider::before,
.social-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #dee2e6;
}

.google-btn-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}
.google-btn-wrapper > div,
.google-btn-wrapper iframe {
  width: 100% !important;
  min-width: 100% !important;
}
.btn-google-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #dadce0;
  color: #3c4043;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  gap: 0.5rem;
  transition: background 0.2s, border-color 0.2s;
}
.btn-google-fallback:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #c6c6c6;
}
.btn-facebook {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1877f2;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.6rem 1rem;
  gap: 0.5rem;
  transition: background 0.2s;
}
.btn-facebook:hover:not(:disabled) {
  background: #1464d0;
}
</style>